/**
 * CENTRALIZED ICON SYSTEM
 *
 * Single source of truth for all icons used throughout the app.
 *
 * Benefits:
 * - Change any icon globally by updating one line
 * - Type-safe icon references
 * - IDE autocomplete for all available icons
 * - Easy to find all usages of an icon
 * - Consistent icon usage across components
 *
 * Usage:
 * import { AppIcons } from '@/constants/icons';
 * <AppIcons.Polish className="icon-md" aria-label="Nail Polish" />
 */
import { Link } from 'react-router-dom';
import {
  // Item Types
  Droplet,
  Droplets,
  Square,
  Wrench,
  Sticker,
  Gem,
  Brush,

  // Categories
  Layers,
  Sparkles,
  Shuffle,
  Zap,
  Star,

  // Actions
  Heart,
  Info,
  Edit,
  Trash2,
  Tag,
  Archive,
  SlidersHorizontal,
  AlertTriangle,

  // Navigation
  Package,
  Palette,
  Search,
  ShoppingBag,
  Calendar,
  HelpCircle,
  BookOpen,
  Lightbulb,
  Settings,
  LogOut,
  Home,
  Store,
  Castle,
  StickyNote,

  // Status & Seasonal
  Circle,
  DollarSign,
  Flower,
  Sun,
  Leaf,
  Snowflake,

  // User & Auth
  Hand,
  User,
  Camera,
  Users,

  // Messaging & Other
  Bell,
  BellOff,
  Plus,
  PlusCircle,
  Check,
  CheckCircle,

  // Difficulty
  Sprout,
  TreePine,

  // Brand Status
  Shield,
  ShieldCheck,
  ShieldQuestion,

  // Social & Links
  Globe,
  Instagram,
  ExternalLink,
  Mail,
  Copy,
  MapPin,
  Share2,
  Flag,

  // General UI
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Filter,
  SortAsc,
  SortDesc,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';

export const AppIcons = {
  // ===== ITEM TYPES =====
  Polish: Droplet,
  Plate: Square,
  Tool: Wrench,
  Acrylic: Brush,
  Decal: Sticker,
  Embellishment: Gem,
  Powder: Sparkles,
  CuticleCare: Droplets,
  Creation: Palette,
  PressOns: Hand,
  Jewelry: Gem,

  // ===== TAG CATEGORIES =====
  BaseType: Layers,
  Finish: Sparkles,
  HoloType: Sparkles, // Same as Finish (rainbow not available in Lucide)
  ColorShift: Shuffle,
  Particles: Gem,
  SpecialFeatures: Zap,
  ColorCharacter: Star,
  Utility: Wrench,

  // ===== BRAND CATEGORIES =====
  Indie: Palette,
  Boutique: Sparkles,
  Mainstream: Store,
  Unknown: HelpCircle,

  // ===== BRAND STATUS =====
  BrandVerified: ShieldCheck,
  BrandPending: Shield,
  BrandCommunity: ShieldQuestion,

  // ===== BRAND ACTIONS =====
  Follow: Bell,
  Unfollow: BellOff,
  Share: Share2,
  Report: Flag,
  Collectors: Users,

  // ===== SOCIAL & LINKS =====
  Website: Globe,
  InstagramIcon: Instagram,
  ExternalLinkIcon: ExternalLink,
  MailIcon: Mail,
  CopyIcon: Copy,
  Location: MapPin,

  // ===== DIFFICULTY LEVELS =====
  Beginner: Sprout,
  Intermediate: Leaf,
  Advanced: TreePine,

  // ===== ACTIONS =====
  Favorite: Heart,
  Wishlist: Star,
  RareLE: Gem,
  NeedsInfo: Info,
  EditAction: Edit,
  Delete: Trash2,
  Tags: Tag,
  Archive: Archive,
  Controls: SlidersHorizontal,
  Warning: AlertTriangle,
  Lost: HelpCircle,
  UsedUp: CheckCircle,

  // ===== STATUS =====
  Available: Circle,
  Pending: Circle,
  Sold: Circle,
  SoldAction: DollarSign,

  // ===== SEASONAL =====
  Spring: Flower,
  Summer: Sun,
  Fall: Leaf,
  Winter: Snowflake,

  // ===== NAVIGATION =====
  ViewAll: Package,
  Creations: Palette,
  Inspo: Sparkles,
  Wants: Star,
  Explore: Sparkles,
  Discover: Search,
  Marketplace: ShoppingBag,
  Events: Calendar,
  Help: HelpCircle,
  Guides: BookOpen,
  Feedback: Lightbulb,
  SettingsNav: Settings,
  SignOut: LogOut,
  Home: Home,
  Brands: Castle,
  Announcements: StickyNote,
  Contact: Mail,

  // ===== NOTIFICATIONS =====
  WishlistAlert: Star, // Consistent with Wishlist icon
  DestashInquiry: Tag,
  Notification: Bell,

  // ===== INFORMATIONAL =====
  Info: Info,
  Tip: Lightbulb,
  SearchIcon: Search,
  Color: Palette,

  // ===== MESSAGING =====
  CreationsAdded: PlusCircle,
  StatusUpdated: Tag,

  // ===== USER & AUTH =====
  Welcome: Hand,
  Join: Sparkles,
  UserIcon: User,
  ProfilePicture: Camera,

  // ===== GENERAL UI =====
  Close: X,
  DropdownOpen: ChevronDown,
  DropdownClosed: ChevronRight,
  NavPrev: ChevronLeft,
  NavNext: ChevronRight,
  Loading: Loader2,
  Add: Plus,
  Confirm: Check,
  FilterIcon: Filter,
  SortAscending: SortAsc,
  SortDescending: SortDesc,
  Error: AlertCircle,
  Back: ArrowLeft,
  BookOpen: BookOpen,
  Link: Link,
};

// Type safety - use this type for props that accept icon names
export type AppIconName = keyof typeof AppIcons;

// Helper to get icon component by name (for dynamic icon rendering)
export const getIcon = (iconName: AppIconName) => AppIcons[iconName];
